import React, { useEffect, useState } from "react";
import { Form, DatePicker, Button, Row, Col, Typography, Table, Select, message } from 'antd';
import { loadingAction } from './../../actions/loading-actions';
import { useDispatch } from "react-redux";
import {
    fetchUniqueSchedulesByOriginDestination,
    fetchBoardingDropStats,
    fetchStandardOrigins,
    fetchStandardDestinations
} from './../../actions/reports.action';
import { Printer } from './../../components/custom-components/custom-component';
import { isValid, isValidArray } from "../../utils/utilities";
import { getAPIDateFormat, getLocalDateAndTime } from './../../utils/date.utils';
import { getRangeOptions } from './reports.module';
import * as XLSX from 'xlsx';
import './stop-analysis.report.scss';

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

let allowedMaxDaysRange = 90;

const renderStandardCityOptions = (cityNames) => {
    let cityOptions = [];
    if (isValidArray(cityNames) && cityNames.length > 0) {
        cityNames.forEach((name) => {
            if (isValid(name)) {
                cityOptions.push(<Option key={name} value={name}>{name}</Option>);
            }
        });
    }
    return cityOptions;
};

const renderScheduleOptions = (schedules) => {
    let scheduleOptions = [];
    if (isValidArray(schedules) && schedules.length > 0) {
        schedules.forEach(element => {
            if (isValid(element)) {
                scheduleOptions.push(
                    <Option key={element.id} value={element.id}>
                        {element.display_name}
                    </Option>
                );
            }
        });
    }
    return scheduleOptions;
};

const generateExport = (filters, statsData) => {
    if (!isValidArray(statsData) || !statsData.cities) return;

    const rows = [];
    const headers = ["City", "Location", "Total Boarding", "Avg Boarding", "Total Drop", "Avg Drop"];
    rows.push(headers);

    statsData.cities.forEach((city) => {
        // Determine is_boarding_city and is_drop_city from locations
        let is_boarding_city = false;
        let is_drop_city = false;
        
        if (isValidArray(city.locations)) {
            // Check if any location has is_boarding_point or is_drop_point as true
            is_boarding_city = city.locations.some(loc => loc.is_boarding_point === true);
            is_drop_city = city.locations.some(loc => loc.is_drop_point === true);
        }
        
        // City header row - only show boarding/drop stats if is_boarding_city/is_drop_city is true
        rows.push([
            city.city,
            "",
            (is_boarding_city && city.total_boarding !== null) ? city.total_boarding : "",
            (is_boarding_city && city.avg_boarding !== null) ? city.avg_boarding : "",
            (is_drop_city && city.total_drop !== null) ? city.total_drop : "",
            (is_drop_city && city.avg_drop !== null) ? city.avg_drop : ""
        ]);

        // Location rows - only show boarding/drop stats if is_boarding_point/is_drop_point is true
        if (isValidArray(city.locations)) {
            city.locations.forEach(loc => {
                rows.push([
                    "",
                    loc.location,
                    (loc.is_boarding_point && loc.total_boarding !== null) ? loc.total_boarding : "",
                    (loc.is_boarding_point && loc.avg_boarding !== null) ? loc.avg_boarding : "",
                    (loc.is_drop_point && loc.total_drop !== null) ? loc.total_drop : "",
                    (loc.is_drop_point && loc.avg_drop !== null) ? loc.avg_drop : ""
                ]);
            });
        }
    });

    const worksheet = XLSX.utils.aoa_to_sheet([
        [],
        ["Report", "From Date", "To Date", "Generated Time"],
        ["Stop Analysis Report", filters.fromDate, filters.toDate, filters.generatedTime],
        ["Trip Count", statsData.trip_count || 0],
        [],
        ...rows
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stop Analysis Report');
    XLSX.writeFile(workbook, 'stop-analysis-report.xlsx');
};

const RenderStatsTable = ({ statsData, filters }) => {
    if (!isValidArray(statsData) || !statsData.cities || statsData.cities.length === 0) {
        return null;
    }

    const buildTableData = () => {
        const rows = [];
        statsData.cities.forEach((city, cityIndex) => {
            // Determine is_boarding_city and is_drop_city from locations
            let is_boarding_city = false;
            let is_drop_city = false;
            
            if (isValidArray(city.locations)) {
                // Check if any location has is_boarding_point or is_drop_point as true
                is_boarding_city = city.locations.some(loc => loc.is_boarding_point === true);
                is_drop_city = city.locations.some(loc => loc.is_drop_point === true);
            }
            
            // Add city row
            rows.push({
                key: `city-${cityIndex}`,
                isCity: true,
                index: city.index,
                city: city.city,
                location: city.city,
                total_boarding: city.total_boarding,
                avg_boarding: city.avg_boarding,
                total_drop: city.total_drop,
                avg_drop: city.avg_drop,
                is_boarding_city: is_boarding_city,
                is_drop_city: is_drop_city
            });

            // Add location rows
            if (isValidArray(city.locations)) {
                city.locations.forEach((loc, locIndex) => {
                    rows.push({
                        key: `loc-${cityIndex}-${locIndex}`,
                        isCity: false,
                        city: city.city,
                        location: loc.location,
                        total_boarding: loc.total_boarding,
                        avg_boarding: loc.avg_boarding,
                        total_drop: loc.total_drop,
                        avg_drop: loc.avg_drop,
                        is_boarding_point: loc.is_boarding_point,
                        is_drop_point: loc.is_drop_point
                    });
                });
            }
        });
        return rows;
    };

    const columns = [
        {
            title: '',
            dataIndex: 'index',
            key: 'index',
            width: 50,
            render: (value, record) => {
                if (record.isCity) {
                    return <span className="city-index">{value}</span>;
                }
                return <span className="location-dot">•</span>;
            }
        },
        {
            title: '',
            dataIndex: 'location',
            key: 'location',
            render: (value, record) => {
                if (record.isCity) {
                    return <strong className="city-name">{value}</strong>;
                }
                return <span className="location-name">{value}</span>;
            }
        },
        {
            title: 'Total Boarding',
            dataIndex: 'total_boarding',
            key: 'total_boarding',
            width: 100,
            align: 'center',
            render: (value, record) => {
                // For city rows, check is_boarding_city; for location rows, check is_boarding_point
                if (value === null) return null;
                if (record.isCity && !record.is_boarding_city) return null;
                if (!record.isCity && !record.is_boarding_point) return null;
                return <span className="stat-value boarding">{value}</span>;
            }
        },
        {
            title: 'Avg. Boarding',
            dataIndex: 'avg_boarding',
            key: 'avg_boarding',
            width: 100,
            align: 'center',
            render: (value, record) => {
                // For city rows, check is_boarding_city; for location rows, check is_boarding_point
                if (value === null) return null;
                if (record.isCity && !record.is_boarding_city) return null;
                if (!record.isCity && !record.is_boarding_point) return null;
                return <span className="stat-value avg-boarding">{value}</span>;
            }
        },
        {
            title: 'Total Drop',
            dataIndex: 'total_drop',
            key: 'total_drop',
            width: 100,
            align: 'center',
            render: (value, record) => {
                // For city rows, check is_drop_city; for location rows, check is_drop_point
                if (value === null) return null;
                if (record.isCity && !record.is_drop_city) return null;
                if (!record.isCity && !record.is_drop_point) return null;
                return <span className="stat-value drop">{value}</span>;
            }
        },
        {
            title: 'Avg. Drop',
            dataIndex: 'avg_drop',
            key: 'avg_drop',
            width: 100,
            align: 'center',
            render: (value, record) => {
                // For city rows, check is_drop_city; for location rows, check is_drop_point
                if (value === null) return null;
                if (record.isCity && !record.is_drop_city) return null;
                if (!record.isCity && !record.is_drop_point) return null;
                return <span className="stat-value avg-drop">{value}</span>;
            }
        }
    ];

    return (
        <div className="stats-table-container">
            <Row justify="space-between" className="report-header">
                <Col>
                    <Text><Text strong>From Date:</Text> {filters.fromDate}</Text>
                </Col>
                <Col>
                    <Text><Text strong>To Date:</Text> {filters.toDate}</Text>
                </Col>
                <Col>
                    <Text><Text strong>Trip Count:</Text> {statsData.trip_count || 0}</Text>
                </Col>
            </Row>
            <Table
                className="stop-analysis-table"
                columns={columns}
                dataSource={buildTableData()}
                pagination={false}
                size="small"
                rowClassName={(record) => record.isCity ? 'city-row' : 'location-row'}
            />
        </div>
    );
};

export const StopAnalysisReport = () => {
    const [schedules, setSchedules] = useState([]);
    const [statsData, setStatsData] = useState(null);
    const [filters, setFilters] = useState({});
    const [showSchedules, setShowSchedules] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [standardOrigins, setStandardOrigins] = useState([]);
    const [standardDestinations, setStandardDestinations] = useState([]);
    const [hasOriginSelected, setHasOriginSelected] = useState(false);

    const dispatch = useDispatch();

    const setLoading = (bool) => {
        dispatch(loadingAction(bool));
    };

    const [form] = Form.useForm();
    const [scheduleForm] = Form.useForm();

    useEffect(() => {
        fetchStandardOrigins(
            (list) => setStandardOrigins(list),
            () => setStandardOrigins([])
        );
    }, []);

    const onOriginChange = (value) => {
        form.setFieldsValue({ destination: undefined });
        setStandardDestinations([]);
        const hasOrigin = isValid(value) && String(value).trim() !== '';
        setHasOriginSelected(hasOrigin);
        if (hasOrigin) {
            fetchStandardDestinations(
                value,
                (list) => setStandardDestinations(list),
                () => setStandardDestinations([])
            );
        }
    };

    const onSearchSuccess = (data) => {
        setLoading(false);
        if (isValidArray(data) && data.length > 0) {
            setSchedules(data);
            setShowSchedules(true);
            setShowReport(false);
            setStatsData(null);
        } else {
            message.info("No schedules found for the selected criteria");
            setSchedules([]);
            setShowSchedules(false);
        }
    };

    const onSearchFailure = (error) => {
        setLoading(false);
        message.error(error || "Failed to fetch schedules");
        setSchedules([]);
        setShowSchedules(false);
    };

    const onFinish = (values) => {
        let { origin, destination, dateRange } = values;

        if (!isValidArray(dateRange)) {
            message.error("Please select a date range");
            return;
        }

        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);

        setLoading(true);
        setFilters({
            fromDate,
            toDate,
            origin,
            destination,
            generatedTime: getLocalDateAndTime(new Date())
        });

        fetchUniqueSchedulesByOriginDestination(
            origin,
            destination,
            fromDate,
            toDate,
            onSearchSuccess,
            onSearchFailure
        );
    };

    const onStatsSuccess = (data) => {
        setLoading(false);
        setStatsData(data);
        setShowReport(true);
    };

    const onStatsFailure = (error) => {
        setLoading(false);
        message.error(error || "Failed to fetch stop analysis data");
        setStatsData(null);
        setShowReport(false);
    };

    const onScheduleSelect = (values) => {
        let { scheduleId } = values;

        if (!isValid(scheduleId)) {
            message.error("Please select a schedule");
            return;
        }

        setLoading(true);
        fetchBoardingDropStats(
            scheduleId,
            filters.fromDate,
            filters.toDate,
            onStatsSuccess,
            onStatsFailure
        );
    };

    return (
        <div className="report-inner-div stop-analysis-report">
            <Form onFinish={onFinish} form={form}>
                <Row gutter={[16, 16]} align="middle">
                    <Col>
                        <Form.Item name="origin" label="">
                            <Select
                                showSearch
                                placeholder="Origin"
                                optionFilterProp="children"
                                style={{ width: 200 }}
                                allowClear
                                onChange={onOriginChange}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {renderStandardCityOptions(standardOrigins)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item name="destination" label="">
                            <Select
                                showSearch
                                placeholder="Destination"
                                optionFilterProp="children"
                                style={{ width: 200 }}
                                allowClear
                                disabled={!hasOriginSelected}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {renderStandardCityOptions(standardDestinations)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item
                            name="dateRange"
                            label="Date range"
                            rules={[
                                { required: true, message: 'Please select date range!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || (value[1].diff(value[0], "days") <= allowedMaxDaysRange)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(`Please select a date range within ${allowedMaxDaysRange} days`));
                                    },
                                })
                            ]}
                        >
                            <RangePicker ranges={getRangeOptions(allowedMaxDaysRange)} />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item>
                            <Button type="primary" htmlType="submit">Search</Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {showSchedules && isValidArray(schedules) && schedules.length > 0 && (
                <Form form={scheduleForm} onFinish={onScheduleSelect}>
                    <Row gutter={[16, 16]} align="middle" className="schedule-select-row">
                        <Col>
                            <Form.Item
                                name="scheduleId"
                                label="Schedule"
                                rules={[{ required: true, message: 'Please select a schedule!' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Select Schedule"
                                    optionFilterProp="children"
                                    style={{ width: 400 }}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {renderScheduleOptions(schedules)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button type="primary" htmlType="submit">Get Stats</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}

            {showReport && statsData && (
                <div className="report-actions">
                    <Row gutter={16} className="marginBottom">
                        <Col>
                            <Printer
                                showComponent={false}
                                renderComponent={() => (
                                    <RenderStatsTable statsData={statsData} filters={filters} />
                                )}
                                zoomValue={0.6}
                            />
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                onClick={() => generateExport(filters, statsData)}
                            >
                                Export
                            </Button>
                        </Col>
                    </Row>
                    <RenderStatsTable statsData={statsData} filters={filters} />
                </div>
            )}
        </div>
    );
};

